import { z } from 'zod'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { proposalVersionStatusSchema } from '../enums/proposal-version-status.enum.js'
import { seasonSchema } from '../enums/season.enum.js'
import { proposalWorkSchema } from '../enums/proposal-work.enum.js'
import { documentDtoSchema } from './document.dto.js'
import { siteVisitRefDtoSchema } from './site-visit.dto.js'
import { proposalSelectionDtoSchema } from './proposal-selection.dto.js'
import {
  proposalDeliveryDtoSchema,
  proposalRecipientDtoSchema,
} from './proposal-delivery.dto.js'

/** A site a version covers, trimmed to what a proposal needs to show. */
export const proposalSiteRefDtoSchema = z.object({
  siteId: uuidSchema,
  name: z.string(),
  /**
   * The visits its tree work and its irrigation repairs come from, or null
   * when the site has no visit of that kind yet. A draft not pinned to one
   * shows the site's current visit, which is the one it follows.
   */
  treeVisit: siteVisitRefDtoSchema.nullable(),
  irrigationVisit: siteVisitRefDtoSchema.nullable(),
  /**
   * A later visit of that kind than the one this is built from, for a draft
   * to offer to switch to. Null when it is built from the newest.
   */
  newerTreeVisit: siteVisitRefDtoSchema.nullable(),
  newerIrrigationVisit: siteVisitRefDtoSchema.nullable(),
})

export type ProposalSiteRefDto = z.infer<typeof proposalSiteRefDtoSchema>

/**
 * One revision of a proposal. `status` is derived, never stored — see
 * `PROPOSAL_VERSION_STATUSES`.
 *
 * `isEditable` is included so the UI doesn't have to re-derive the rule that a
 * sent version is frozen. The API is the authority on that; the client just
 * renders it.
 */
export const proposalVersionDtoSchema = z.object({
  id: uuidSchema,
  proposalId: uuidSchema,
  versionNumber: z.number().int().positive(),
  status: proposalVersionStatusSchema,
  summary: z.string().nullable(),
  validUntil: z.string().datetime().nullable(),
  sentAt: z.string().datetime().nullable(),
  acceptedAt: z.string().datetime().nullable(),
  declinedAt: z.string().datetime().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string().datetime(),
  isEditable: z.boolean(),
  sites: z.array(proposalSiteRefDtoSchema),
  documents: z.array(documentDtoSchema),
  /**
   * Every attempt to email this version, newest first.
   *
   * On the version rather than the proposal because that is what was sent: a
   * later version supersedes an earlier one, and the record of where each went
   * has to stay attached to the thing that went.
   */
  deliveries: z.array(proposalDeliveryDtoSchema),
  /**
   * What the client accepted, when they answered through the shared link.
   *
   * Null for a version nobody has answered, and for one somebody in the
   * office marked accepted on their behalf — this is the client's own answer,
   * which is a different fact from the office recording one.
   */
  selection: proposalSelectionDtoSchema.nullable(),
})

export type ProposalVersionDto = z.infer<typeof proposalVersionDtoSchema>

/**
 * A proposal, as shown in a list. The summary fields describe its LATEST
 * version, because that is what someone scanning a list wants to know — "where
 * does this proposal stand" is a question about the live revision.
 */
export const proposalDtoSchema = z.object({
  id: uuidSchema,
  clientId: uuidSchema,
  clientName: z.string(),
  title: z.string(),
  /**
   * Whether this proposal's price is broken out by area.
   *
   * A staff choice. The client never sets it. A sent version freezes the
   * value it went out with, so changing this later governs the next draft.
   */
  itemizeByArea: z.boolean(),
  /**
   * What this proposal bids. A staff choice, like `itemizeByArea`: a new
   * proposal starts from what its sites have, and a sent version freezes the
   * value it went out with.
   */
  work: proposalWorkSchema,
  /**
   * The one season this proposal bids, when it rebids work an earlier
   * proposal estimated for later (docs/seasonal-work.md). Absent or null for
   * an ordinary proposal, which bids every season.
   */
  season: seasonSchema.nullable().optional(),
  ownerId: uuidSchema.nullable(),
  ownerName: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),

  versionCount: z.number().int().nonnegative(),
  latestVersionNumber: z.number().int().nonnegative(),
  latestStatus: proposalVersionStatusSchema.nullable(),
  latestSentAt: z.string().datetime().nullable(),
  siteNames: z.array(z.string()),
  /**
   * What the latest version is worth, tax included, as an exact decimal
   * string: the amount the client agreed to if they accepted through the
   * link, otherwise the full price it was sent at. Null for a draft, whose
   * price is still moving, and for a version sent before prices were kept.
   */
  amount: moneyAmountSchema.nullable(),
  /** When the client last opened the latest version's link, if ever. */
  lastViewedAt: z.string().datetime().nullable(),
  /**
   * Whether the latest version went out as a link. Without one there is
   * nothing to open, so "not opened" would be a claim about a proposal
   * somebody handed over in person.
   */
  wasEmailed: z.boolean(),
})

export type ProposalDto = z.infer<typeof proposalDtoSchema>

export const proposalListDtoSchema = z.object({
  proposals: z.array(proposalDtoSchema),
  total: z.number().int().nonnegative(),
})

export type ProposalListDto = z.infer<typeof proposalListDtoSchema>

/** A proposal with every version, newest first. */
export const proposalDetailDtoSchema = proposalDtoSchema.extend({
  versions: z.array(proposalVersionDtoSchema),
  /**
   * Who this would be emailed to. Null when the client has no live primary
   * contact at all — distinct from a contact with no address, which is a
   * recipient with a null `email`. The two need different advice, so they
   * stay different states.
   */
  recipient: proposalRecipientDtoSchema.nullable(),
})

export type ProposalDetailDto = z.infer<typeof proposalDetailDtoSchema>
